// Renders the prescriber's typed name into a PNG data URL, so the order sent to
// the fulfillment partner carries the signature as an image rather than as text.
// The values below mirror `.signature-text` in GenericSampleRequestForm.css; if
// that rule changes, change these too or the submitted image stops matching what
// the prescriber saw when they signed.
const FONT_FAMILY = "'Dancing Script', 'Brush Script MT', 'Segoe Script', cursive";
const FONT_SIZE_PX = 36;
const FONT_WEIGHT = 500;
const COLOR = "#1f2937";
const PADDING_X = 16;
const PADDING_Y = 12;

// Rendering at 2x keeps the strokes legible if the partner scales the image up
// for a printed order form.
const SCALE = 2;

const font = () => `${FONT_WEIGHT} ${FONT_SIZE_PX}px ${FONT_FAMILY}`;

/**
 * Dancing Script arrives from Google Fonts, so it may not be ready when the
 * prescriber submits. Canvas has no fallback chain like CSS does: it silently
 * draws in a default sans-serif instead, which would ship a signature image
 * that doesn't look like the one on screen. Waiting for the load avoids that.
 */
async function waitForSignatureFont(): Promise<void> {
  if (!("fonts" in document)) return;
  try {
    await document.fonts.load(font());
  } catch {
    // Fall through and render with whatever the browser resolves. A signature
    // in the wrong typeface is recoverable; blocking the submit is not.
  }
}

/**
 * Render `name` as a signature and return a "data:image/png;base64,..." URL.
 * Returns an empty string if the canvas is unavailable, letting callers submit
 * without an image rather than fail outright.
 */
export async function renderSignatureImage(name: string): Promise<string> {
  const text = name.trim();
  if (!text) return "";

  await waitForSignatureFont();

  const canvas = document.createElement("canvas");
  const measureCtx = canvas.getContext("2d");
  if (!measureCtx) return "";

  measureCtx.font = font();
  const metrics = measureCtx.measureText(text);

  // Ascent/descent rather than font size: script faces have long descenders
  // that a font-size-tall canvas would clip.
  const ascent = metrics.actualBoundingBoxAscent || FONT_SIZE_PX;
  const descent = metrics.actualBoundingBoxDescent || FONT_SIZE_PX * 0.35;

  const width = Math.ceil(metrics.width) + PADDING_X * 2;
  const height = Math.ceil(ascent + descent) + PADDING_Y * 2;

  canvas.width = width * SCALE;
  canvas.height = height * SCALE;

  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  ctx.scale(SCALE, SCALE);

  // Knipper renders this onto a paper order form, so a transparent background
  // would come out as whatever is underneath it.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);

  ctx.font = font();
  ctx.fillStyle = COLOR;
  ctx.textBaseline = "alphabetic";
  ctx.fillText(text, PADDING_X, PADDING_Y + ascent);

  return canvas.toDataURL("image/png");
}
